import {
  Modal,
  Card,
  Box,
  IconButton,
  Typography,
  CardContent,
  Select,
  MenuItem,
  TextField,
  Button,
  styled,
  Stack,
  Autocomplete,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { modalStyle } from "../../../_utils/modalStyle";
import { HighlightOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import ControlledDatePicker from "../../forms/ControlledDatePicker";
import CustomButtonModal from "../../common/CustomBtnModal";
import { toastError } from "../../../_utils/toastError";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { ProfileContext } from "../../../contexts/ProfileContext";
import formatTasks from "../../../_utils/formatter/taskFormatter";
import { toast } from "react-toastify";

const TasksModalList: React.FC<any> = ({ open, close, groups }) => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const pId = useSelector((state: any) => state.user.person_id);
  const { id } = useParams();
  const personId = id ?? pId;
  const { setTasks } = useContext(ProfileContext);
  const [confirm, setConfirm] = useState(false);

  const taskGroups = useMemo(
    () =>
      groups
        .filter((el: any) => el.canCreate)
        .map((group: any) => ({
          id: group["@id"],
          label: group.label,
          defaultTasks: group.defaultTasks,
        })) ?? [],
    [groups]
  );

  const methods = useForm({
    defaultValues: {
      tasks: [
        {
          label: "",
          description: "",
          dueDate: new Date(),
          group: "",
          uid: Date.now(),
          defaultTasks: [],
        },
      ],
    },
  });

  const { fields, remove, append } = useFieldArray({
    control: methods.control,
    name: "tasks",
  });

  const addField = () => {
    append({
      label: "",
      description: "",
      dueDate: new Date(),
      group: "",
      uid: Date.now(),
      defaultTasks: [],
    });
  };

  const handleSubmit = (data: any) => {
    if (data.tasks.length > 1) {
      setConfirm(true);
    } else {
      handleData(data);
    }
  };

  const handleData = async (data: any) => {
    setLoading(true);

    try {
      const tasks = data.tasks.map((task: any) => ({
        title: task.label,
        description: task.description,
        dueDate: task.dueDate.toISOString(),
        taskGroup: task.group,
        person: `/api/people/${personId}`,
        finished: false,
      }));

      const calls = tasks.map((task: any) => axios.post(`tasks`, task));

      const responses = await Promise.allSettled(calls);

      const fulfilledTasks = responses
        .filter((response) => response.status === "fulfilled")
        .map((response: any) => response.value.data);

      setTasks((groups: any) =>
        formatTasks(
          groups.map((group: any) => {
            // Filter tasks that belong to this group
            const newTasks = fulfilledTasks.filter(
              (t) => t.taskGroup.id === group.id
            );

            return {
              ...group,
              tasks: [...group.tasks, ...newTasks],
            };
          })
        )
      );
      toast.success(t("todo.added", { count: tasks.length }));
    } catch {
      toastError();
    }

    setLoading(false);
    close();
  };

  const setDefaultTasks = (tasks: any, index: number) => {
    methods.setValue(`tasks.${index}.defaultTasks`, tasks);
  };

  const handleDefaultSelect = (task: any, index: number) => {
    methods.setValue(`tasks.${index}.dueDate`, new Date(task.dueDate));
  };

  useEffect(() => {
    methods.reset({
      tasks: [
        {
          label: "",
          description: "",
          dueDate: new Date(),
          group: "",
          uid: Date.now(),
          defaultTasks: [],
        },
      ],
    });
    setConfirm(false);
  }, [methods, open]);

  const confirmSubmit = () => {
    const values = methods.watch();
    handleData(values);
  };

  return (
    <Modal open={open}>
      <Card
        sx={{
          ...modalStyle,
        }}
      >
        <Box display="flex" alignItems="center" sx={{ p: 1 }}>
          <IconButton aria-label="close" onClick={close} sx={{ ml: "auto" }}>
            <HighlightOff />
          </IconButton>
        </Box>
        <Typography textAlign="center" variant="h5" sx={{ mt: -4 }}>
          {t("todo.new_plan")}
        </Typography>
        <CardContent>
          {!confirm ? (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {fields.map((field, index) => (
                    <TaskItem
                      key={field.uid}
                      control={methods.control}
                      index={index}
                      taskGroups={taskGroups}
                      remove={remove}
                      defaultTasks={methods.watch(
                        `tasks.${index}.defaultTasks`
                      )}
                      setDefaultTasks={(tasks: any) =>
                        setDefaultTasks(tasks, index)
                      }
                      handleDefaultSelect={handleDefaultSelect}
                    />
                  ))}
                </Stack>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="100%"
                  marginTop={2}
                >
                  <Button variant="outlined" onClick={addField}>
                    {t("todo.add")}
                  </Button>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  justifyContent="center"
                  sx={{ pt: 2 }}
                >
                  <CustomButtonModal
                    secondary
                    type="button"
                    onClick={close}
                    variant="outlined"
                    color="secondary"
                    text={t(`btn.cancel`)}
                  />
                  <CustomButtonModal
                    type="submit"
                    variant="contained"
                    text={t("btn.valid")}
                    disabled={loading}
                  />
                </Box>
              </form>
            </FormProvider>
          ) : (
            <div>
              <Typography textAlign="center">
                {t("todo.add_these", { count: fields.length })}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="center"
                sx={{ pt: 2 }}
              >
                <CustomButtonModal
                  secondary
                  type="button"
                  onClick={() => setConfirm(false)}
                  variant="outlined"
                  color="secondary"
                  text={t(`btn.cancel`)}
                />
                <CustomButtonModal
                  onClick={confirmSubmit}
                  variant="contained"
                  text={t("btn.valid")}
                  disabled={loading}
                />
              </Box>
            </div>
          )}
        </CardContent>
      </Card>
    </Modal>
  );
};

export default TasksModalList;

const TaskItem: React.FC<any> = ({
  control,
  remove,
  index,
  taskGroups,
  setDefaultTasks,
  defaultTasks,
  handleDefaultSelect,
}) => {
  const { t } = useTranslation("common");

  return (
    <Form>
      <Controller
        control={control}
        name={`tasks.${index}.group`}
        rules={{ required: true }}
        render={({ field: { value, onChange }, formState: { errors } }) => (
          <Select
            error={Boolean((errors.tasks as any)?.[index]?.group)}
            sx={{ flex: 1 }}
            value={value}
            onChange={(e) => {
              const group = taskGroups.find(
                (group: any) => group.id === e.target.value
              );
              setDefaultTasks(group?.defaultTasks ?? []);
              onChange(e.target.value);
            }}
            displayEmpty
            renderValue={(selected) => {
              if (selected === "") {
                return <em>{t("todo.select_group")}</em>;
              }
              const selectedGroup = taskGroups.find(
                (group: any) => group.id === selected
              );
              return selectedGroup ? selectedGroup.label : "";
            }}
          >
            <MenuItem value="">
              <em>{t("todo.select_group")}</em>
            </MenuItem>
            {taskGroups.map((group: any) => (
              <MenuItem key={group.id} value={group.id}>
                {group.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />

      <LabelAutoComplete
        control={control}
        index={index}
        options={defaultTasks}
        handleDefaultSelect={(task: any) => handleDefaultSelect(task, index)}
      />

      {/* <TextField
        sx={{ flex: 2 }}
        {...register(`tasks.${index}.label`, { required: true })}
        placeholder="Tâche"
      /> */}
      <ControlledDatePicker
        control={control}
        name={`tasks.${index}.dueDate`}
        label={t("todo.limit_date")}
      />
      <IconButton
        sx={{ width: "25px", height: "25px" }}
        color="error"
        onClick={() => remove(index)}
      >
        <HighlightOff />
      </IconButton>
    </Form>
  );
};

const Form = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  @media screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const LabelAutoComplete: React.FC<any> = ({
  options,
  control,
  index,
  handleDefaultSelect,
}) => {
  const { t } = useTranslation("common");
  return (
    <Controller
      name={`tasks.${index}.label`}
      control={control}
      rules={{ required: true }}
      render={({ field: { value, onChange } }) => (
        <Autocomplete
          freeSolo
          sx={{ flex: 2, width: "100%" }}
          id="free-solo-2-demo"
          disableClearable
          value={{ title: value }}
          options={options}
          getOptionLabel={(option: any) => option.title ?? option.label}
          onChange={(_, newValue: any) => {
            if (newValue?.title) {
              handleDefaultSelect(newValue, index);
              onChange(newValue.title);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("todo.label")}
              onChange={(e) => onChange(e.target.value)}
              value={value}
              // slotProps={{
              //   input: {
              //     ...params.InputProps,
              //     type: "search",
              //   },
              // }}
            />
          )}
        />
      )}
    />
  );
};
